import { GluestackUIProvider } from "@/components/ui/gluestack-ui-provider";
import { defaultTheme, toGluestackTheme } from "@/contexts/defaultTheme";
import { getValueFromStorage } from "@/utils/secureStore";
import type { ParkSkinAPIObject } from "ipctg-inflow-types/InFlowAPIMisc";
import type { ContactType } from "ipctg-inflow-types/InFlowEnums";
import { useColorScheme } from "nativewind";
import type React from "react";
import { type ReactNode, createContext, useEffect, useMemo, useState } from "react";

type ModeType = "light" | "dark" | "system";

interface CustomThemeProviderProps {
  children: ReactNode;
  mode?: ModeType;
}

export interface CustomThemeContextData {
  mode: "dark" | "light";
  skin: ParkSkinAPIObject;
  theme: {
    light: Record<string, string>;
    dark: Record<string, string>;
    defaultContactType: ContactType;
  };
}

export const CustomThemeContext = createContext<
  CustomThemeContextData & {
    setCustomTheme: React.Dispatch<React.SetStateAction<CustomThemeContextData>>;
  }
>({
  mode: "light",
  skin: defaultTheme,
  theme: toGluestackTheme(defaultTheme.theme),
  setCustomTheme: () => {},
});

const getColorSchemeName = (colorScheme: "light" | "dark" | undefined, mode: ModeType): "light" | "dark" => {
  if (mode === "system") {
    return colorScheme ?? "light";
  }
  return mode;
};

export function CustomThemeProvider({ children, mode = "light" }: Readonly<CustomThemeProviderProps>): React.JSX.Element {
  const { colorScheme } = useColorScheme();
  const [customTheme, setCustomTheme] = useState<CustomThemeContextData>({
    mode: getColorSchemeName(colorScheme, mode),
    skin: defaultTheme,
    theme: toGluestackTheme(defaultTheme.theme),
  });

  useEffect(() => {
    const calcMode = getColorSchemeName(colorScheme, mode);
    setCustomTheme((prev) => ({
      ...prev,
      mode: calcMode,
      theme: toGluestackTheme(prev.skin.theme),
    }));
  }, [colorScheme, mode]);

  useEffect(() => {
    const loadTheme = async () => {
      try {
        const storedTheme = await getValueFromStorage("skin");
        if (storedTheme) {
          const parsedTheme = JSON.parse(storedTheme || "{}");
          setCustomTheme({
            mode: getColorSchemeName(colorScheme, mode),
            skin: parsedTheme,
            theme: toGluestackTheme(parsedTheme.theme),
          });
        } else {
          setCustomTheme({
            mode: getColorSchemeName(colorScheme, mode),
            skin: defaultTheme,
            theme: toGluestackTheme(defaultTheme.theme),
          });
        }
      } catch (error) {
        console.error("Error setting theme:", error);
      }
    };
    loadTheme();
  }, [colorScheme, mode]);

  return (
    <CustomThemeContext.Provider value={useMemo(() => ({ ...customTheme, setCustomTheme }), [customTheme])}>
      <GluestackUIProvider config={customTheme.theme} mode={customTheme.mode}>
        {children}
      </GluestackUIProvider>
    </CustomThemeContext.Provider>
  );
}

export default CustomThemeProvider;
